import { useCallback, useEffect } from 'react';

export const MIN_BID = 2;
export const MAX_BID = 99;

/** Digits only, and never more than the two a 2..99 bid can use. */
export const sanitizeBid = (raw) => String(raw ?? '').replace(/\D/g, '').slice(0, 2);

/**
 * The bid a raw field value stands for, or null when it isn't one yet.
 * Parses rather than repairs: '100' is not a bid of 10.
 */
export function parseBid(raw) {
  const digits = String(raw ?? '').trim();
  if (!/^\d{1,2}$/.test(digits)) return null;
  const n = parseInt(digits, 10);
  return n >= MIN_BID && n <= MAX_BID ? n : null;
}

/** True when keystrokes belong to whatever the user is typing in, not to the game. */
export function isTextField(el) {
  const tag = el?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable === true;
}

// Both bid inputs are mounted at once — the mobile and desktop layouts hide
// each other with display:none rather than unmounting — so a keystroke has to
// pick the one on screen. jsdom has no layout and reports offsetParent null
// for everything, hence the fall back to the first mounted input.
function visibleInput(inputRefs) {
  const mounted = inputRefs.map(r => r?.current).filter(Boolean);
  return mounted.find(el => el.offsetParent !== null) ?? mounted[0] ?? null;
}

/**
 * Type a bid the moment you think of one: any digit key grabs the bid field
 * and starts the number there, Enter places it, Escape backs out. Bidding is
 * the race in this game, so the keyboard should never cost a mouse trip.
 *
 * One instance owns the whole interaction; both panels render `inputProps`.
 */
export default function useBidEntry({ active, value, setValue, seed, inputRefs, onCommit }) {
  const valid = parseBid(value) !== null;

  const commit = useCallback(() => {
    const count = parseBid(value);
    if (count === null) return;
    onCommit(count);
  }, [value, onCommit]);

  useEffect(() => {
    if (!active) return undefined;

    const handler = (e) => {
      if (e.key < '0' || e.key > '9' || e.key.length !== 1) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // Someone is typing in the nickname field — or in the bid field itself,
      // which types normally once focused.
      if (isTextField(e.target)) return;

      const input = visibleInput(inputRefs);
      if (!input) return;

      e.preventDefault();
      // Start a fresh number: the field carries a seeded value nobody typed.
      // The re-render moves the caret to the end, so the next digit appends.
      setValue(e.key);
      input.focus();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [active, inputRefs, setValue]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      commit();
      e.target.blur();
      return;
    }
    if (e.key === 'Escape') {
      // Escape deselects a robot everywhere else; in here it means "forget
      // what I typed", so it must not reach the window handler.
      e.preventDefault();
      e.stopPropagation();
      setValue(seed);
      e.target.blur();
    }
  }, [commit, seed, setValue]);

  const onChange = useCallback((e) => setValue(sanitizeBid(e.target.value)), [setValue]);

  const inputProps = {
    value,
    onChange,
    onKeyDown,
    type: 'text',
    inputMode: 'numeric',
    pattern: '[0-9]*',
    maxLength: 2,
    autoComplete: 'off',
    spellCheck: false,
  };

  return { inputProps, valid, commit };
}
